import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, ArrowRight, Eye, EyeOff, Shield, Gamepad2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await login(data.username, data.password);
      if (result.success) {
        toast.success('ورود موفقیت‌آمیز!');
        navigate('/user-panel');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('خطا در ورود');
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-[#050811] text-white pt-24 pb-16 px-4 flex items-center justify-center relative overflow-hidden" dir="rtl">
      
      {/* پس‌زمینه زنده با هاله‌های زمردی و سیان */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-900/20 rounded-full blur-[140px]" />
        <div className="absolute top-[40%] right-0 w-[500px] h-[300px] bg-cyan-900/15 rounded-full blur-[160px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-950/60 to-cyan-950/60 p-8 text-center border-b border-white/10">
            <motion.div variants={itemVariants}>
              <div className="w-16 h-16 bg-white/10 rounded-2xl border border-white/20 p-2 flex items-center justify-center mx-auto mb-4 shadow-xl">
                <img src="/favicon.ico" alt="AvalonMC Logo" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-black text-white mb-1">
                ورود به حساب کاربری
              </h1>
              <p className="text-gray-400 text-sm">
                به پنل کاربری سرور خوش امدید
              </p>
            </motion.div>
          </div>

          {/* Form */}
          <div className="p-8">
            <motion.form variants={itemVariants} onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  نام کاربری
                </label>
                <div className="relative">
                  <User className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    {...register('username', {
                      required: 'نام کاربری الزامی است',
                      validate: (v) => !v.includes(' ') || 'فاصله مجاز نیست، به جای فاصله از _ استفاده کنید'
                    })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pr-12 pl-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="نام کاربری خود در سرور را وارد کنید"
                  />
                </div>
                {errors.username && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" />
                    {errors.username.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-300 mb-2">
                  رمز عبور
                </label>
                <div className="relative">
                  <Lock className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'رمز عبور الزامی است'
                    })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pr-12 pl-12 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="رمز عبور خود را داخل سرور وارد کنید"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-400 text-xs mt-2 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>ورود</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </motion.form>

            <div className="mt-8 text-center text-xs text-gray-400">
              اگر حساب کاربری ندارید باید از داخل سرور اقدام کنید!
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
